import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchEventShiftRoster } from '@/utils/eventShiftsApi';

/**
 * Who is on each of an event's shifts, as { shiftId: volunteerIds }.
 *
 * Separate from useEventShifts because only the org side needs names: reading
 * `volunteer_shifts` requires being on the event's roster, while the shift list and its
 * counts come from an RPC anyone can call.
 */
export function useEventShiftRoster(eventId: string | null | undefined, enabled = true) {
  const [roster, setRoster] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const eventIdRef = useRef(eventId);
  eventIdRef.current = eventId;

  const load = useCallback(async () => {
    const requestedId = eventId;
    if (!requestedId || !enabled) {
      setRoster({});
      setFailed(false);
      return;
    }
    setLoading(true);
    const result = await fetchEventShiftRoster(requestedId);
    if (eventIdRef.current !== requestedId) return;
    setRoster(result ?? {});
    setFailed(result === null);
    setLoading(false);
  }, [eventId, enabled]);

  useEffect(() => {
    void load();
  }, [load]);

  return { roster, loading, failed, refetch: load };
}
